/**
 * /dictionary — bang pHash cua solver `hashgrid` (muc 12).
 *
 * Day la noi nguoi van hanh nhin thay TOAN BO tri thuc cua solver nay: moi dong la mot
 * anh trong kho cua site, kem nhan va so lan tra trung/truot. Khong co model, khong co
 * hop den — sua mot nhan o day la sua ngay hanh vi cua solver.
 *
 * Thu quan trong nhat tren man hinh: TY LE TRUOT. Khi site doi kho anh, tu dien cu thanh
 * vo dung ngay lap tuc nhung khong co gi bao loi ca — solver chi lang le tra null. Bang
 * bao dong o dau trang la thu duy nhat noi cho nguoi van hanh biet dieu do da xay ra.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ProfileDto } from '@capforge/shared';
import { ErrorBox } from '../components/ErrorBox';
import { Spinner } from '../components/Spinner';
import { api, errorMessage, type DictionaryPage, type RebuildResultDto } from '../lib/api';

export function Dictionary(): JSX.Element {
  const [profiles, setProfiles] = useState<ProfileDto[]>([]);
  const [profileKey, setProfileKey] = useState('');
  const [page, setPage] = useState<DictionaryPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unlabeledOnly, setUnlabeledOnly] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildResult, setRebuildResult] = useState<RebuildResultDto | null>(null);
  const [editing, setEditing] = useState<Record<number, string>>({});

  useEffect(() => {
    api
      .profiles()
      .then((list) => {
        const grid = list.filter((p) => p.solver === 'hashgrid');
        setProfiles(grid.length > 0 ? grid : list);
        if (profileKey === '' && (grid[0] ?? list[0])) {
          setProfileKey((grid[0] ?? list[0])?.key ?? '');
        }
      })
      .catch((err: unknown) => setError(errorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (profileKey === '') return;
    setLoading(true);
    setError(null);
    try {
      setPage(await api.dictionary({ profile: profileKey, unlabeledOnly, limit: 300 }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [profileKey, unlabeledOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveLabel = async (id: number, phash: string, label: string): Promise<void> => {
    try {
      await api.upsertDictionaryEntry({ profileKey, phash, label });
      setEditing((e) => {
        const next = { ...e };
        delete next[id];
        return next;
      });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const remove = async (id: number): Promise<void> => {
    try {
      await api.deleteDictionaryEntry(id);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const rebuild = async (): Promise<void> => {
    setRebuilding(true);
    setError(null);
    try {
      setRebuildResult(await api.rebuildDictionary(profileKey));
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Từ điển pHash</h1>
          <p className="max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            Toàn bộ tri thức của solver <code>hashgrid</code>. Mỗi dòng là một ảnh trong kho
            của site — sửa nhãn ở đây là đổi ngay hành vi của solver, không cần train gì.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={profileKey}
            onChange={(e) => setProfileKey(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
          >
            {profiles.map((p) => (
              <option key={p.key} value={p.key}>
                {p.key}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={unlabeledOnly}
              onChange={(e) => setUnlabeledOnly(e.target.checked)}
            />
            chi dong chua co nhan
          </label>
          <button
            onClick={() => void rebuild()}
            disabled={rebuilding || profileKey === ''}
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
          >
            {rebuilding ? 'đang dựng lại…' : 'Dựng lại từ mẫu đã gán nhãn'}
          </button>
        </div>
      </header>

      {error && <ErrorBox message={error} onRetry={() => void load()} />}

      {page?.alarm && (
        <div className="rounded border border-red-300 bg-red-50 p-4 text-sm dark:border-red-800 dark:bg-red-950/40">
          <p className="font-semibold text-red-700 dark:text-red-300">
            Tỉ lệ trượt {(page.missRate * 100).toFixed(1)}% — site có thể đã đổi kho ảnh
          </p>
          <p className="mt-1 text-red-700/90 dark:text-red-300/90">
            Một kho ảnh ổn định gần như không bao giờ trượt sau khi gán nhãn xong. Mức này
            nghĩa là solver đang gặp ảnh nó chưa từng thấy. Hãy xem trang{' '}
            <strong>Captcha lạ</strong> để gán nhãn cho lô ảnh mới, hoặc hạ profile về{' '}
            <code>shadow</code> cho tới khi từ điển được cập nhật.
          </p>
        </div>
      )}

      {rebuildResult && (
        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
          Da quet {rebuildResult.scanned} mau — them {rebuildResult.created}, cap nhat{' '}
          {rebuildResult.updated}, bo qua {rebuildResult.skippedTooFew} (chua du 3 lan) va{' '}
          {rebuildResult.skippedInconsistent} (nhan mau thuan).
          {rebuildResult.skippedInconsistent > 0 && (
            <span className="block text-amber-700 dark:text-amber-400">
              Nhãn mâu thuẫn nghĩa là cùng một ảnh bị gán hai nhãn khác nhau — hoặc cắt ô sai,
              hoặc hai ảnh khác nhau đụng hash. Cả hai đều cần người nhìn, không phải máy đoán.
            </span>
          )}
        </div>
      )}

      {page && (
        <p className="text-sm text-slate-500">
          {page.total} dong · {page.lookupTotal} lan tra cuu · ty le truot{' '}
          {(page.missRate * 100).toFixed(1)}%
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : page && page.items.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800">
              <tr>
                <th className="px-3 py-2">pHash</th>
                <th className="px-3 py-2">Nhãn</th>
                <th className="px-3 py-2 text-right">Trúng</th>
                <th className="px-3 py-2 text-right">Trượt</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {page.items.map((entry) => {
                const draft = editing[entry.id] ?? entry.label;
                return (
                  <tr key={entry.id} className="border-t border-slate-200 dark:border-slate-700">
                    <td className="px-3 py-1.5 font-mono text-xs">{entry.phash}</td>
                    <td className="px-3 py-1.5">
                      <input
                        value={draft}
                        onChange={(e) =>
                          setEditing((s) => ({ ...s, [entry.id]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveLabel(entry.id, entry.phash, draft);
                        }}
                        placeholder="chưa có nhãn"
                        className="w-full rounded border border-slate-300 px-2 py-1 dark:border-slate-600 dark:bg-slate-800"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">{entry.hits}</td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      <span className={entry.misses > 0 ? 'text-amber-600' : ''}>
                        {entry.misses}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {draft !== entry.label && (
                        <button
                          onClick={() => void saveLabel(entry.id, entry.phash, draft)}
                          className="mr-2 rounded bg-blue-600 px-2 py-0.5 text-xs text-white"
                        >
                          Luu
                        </button>
                      )}
                      <button
                        onClick={() => void remove(entry.id)}
                        className="rounded border border-slate-300 px-2 py-0.5 text-xs dark:border-slate-600"
                      >
                        Xoa
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 py-12 text-center text-sm text-slate-500 dark:border-slate-600">
          <p>Từ điển của profile này đang rỗng.</p>
          <p className="mt-1">
            Gán nhãn mẫu ở trang <strong>Mẫu</strong> rồi bấm{' '}
            <em>Dựng lại từ mẫu đã gán nhãn</em>, hoặc để solver tự ghi lại các hash nó gặp
            và gán nhãn trực tiếp ở đây.
          </p>
        </div>
      )}
    </div>
  );
}
